export const inbox = {
  title: "Inbox",
  searchPlaceholder: "Search comments and messages…",
  semantic: "Smart search",
  semanticHint: "Finds by meaning, not just words: “angry about delivery” finds late-order complaints.",
  smartSearch: {
    unavailable: "Smart search is unavailable right now",
    unavailableHint: "Showing keyword results only. Matches by meaning come back once the search index is healthy again.",
    degraded: "Smart search is partly unavailable",
    degradedHint: "Some messages could not be indexed yet, so results by meaning may be incomplete. Keyword matches are complete.",
  },
  filters: {
    title: "Filters",
    show: "Show filters",
    hide: "Hide filters",
    state: "Status",
    type: "Type",
    typeAll: "All",
    typeComment: "Comments",
    typeConversation: "Messages",
    channel: "Channel",
    account: "Account",
    intent: "Intent",
    sentiment: "Sentiment",
    assignee: "Assignee",
    assigneeAnyone: "Anyone",
    assigneeMe: "Assigned to me",
    assigneeUnassigned: "Unassigned",
    any: "Any",
    clear: "Clear filters",
    active_one: "{{count}} active",
    active_other: "{{count}} active",
    views: "Views",
    viewAll: "All open",
    viewMine: "Mine",
    viewUnassigned: "Unassigned",
    viewNegative: "Needs care",
    aiCategory: "AI category",
    groupWhat: "What",
    groupAi: "AI",
    groupTeam: "Team",
    groupAlso: "Also show",
    onePerson: "One person",
  },
  list: {
    label: "Conversations and comments",
    empty: "Inbox zero 🎉",
    emptyHint: "Nothing matches these filters. New comments and messages appear here live.",
    emptyFiltered: "No matches",
    emptyFilteredHint: "Try clearing a filter or searching for something else.",
    newItems_one: "{{count}} new",
    newItems_other: "{{count}} new",
    loadingMore: "Loading more…",
    end: "You've reached the end",
    hidden: "Hidden",
    deleted: "Deleted",
    voiceNote: "Voice note",
    photo: "Photo",
    sharedPost: "Shared a post",
    attachment: "Attachment",
    you: "You: ",
    comment: "Comment",
    dm: "Message",
    unread: "Unread",
    select: "Select {{name}}",
    replies_one: "{{count}} reply",
    replies_other: "{{count}} replies",
    selectAll: "Select all loaded",
    tools: "Search and filters",
    more: "List options",
    smartOn: "Turn on smart search",
    smartOff: "Turn off smart search",
    smartPlaceholder: "Search by meaning…",
    compactRows: "Compact rows",
    comfortableRows: "Comfortable rows",
  },
  bulk: {
    selected_one: "{{count}} selected",
    selected_other: "{{count}} selected",
    hide: "Hide",
    unhide: "Unhide",
    done: "Done",
    assign: "Assign",
    reply: "Reply to all",
    delete: "Delete",
    replyPlaceholder: "One reply for every selected comment…",
    replySend: "Send replies",
    running_one: "Applying “{{action}}” to {{count}} comment",
    running_other: "Applying “{{action}}” to {{count}} comments",
    finished: "Done: {{action}}",
    deleteTitle_one: "Delete {{count}} comment on Instagram?",
    deleteTitle_other: "Delete {{count}} comments on Instagram?",
    onlyComments: "Bulk actions apply to comments; messages are skipped.",
    // QA F12: reversible bulk actions (toast with Undo while running and after finishing)
    undoable: {
      hide: { running_one: "Hiding {{count}} comment", running_other: "Hiding {{count}} comments", done_one: "Hidden {{count}} comment", done_other: "Hidden {{count}} comments" },
      unhide: { running_one: "Unhiding {{count}} comment", running_other: "Unhiding {{count}} comments", done_one: "Unhid {{count}} comment", done_other: "Unhid {{count}} comments" },
      archive: { running_one: "Archiving {{count}} comment", running_other: "Archiving {{count}} comments", done_one: "Archived {{count}} comment", done_other: "Archived {{count}} comments" },
      unarchive: { running_one: "Restoring {{count}} comment", running_other: "Restoring {{count}} comments", done_one: "Restored {{count}} comment", done_other: "Restored {{count}} comments" },
    },
    undo: "Undo",
    undoStarted: "Undoing… only the comments this action changed are reverted",
    undoFailed: "Couldn't undo",
    tag: "Tag",
    untag: "Remove tag",
    deleteMore: "Delete…",
    doneToast_one: "{{count}} item marked done",
    doneToast_other: "{{count}} items marked done",
    assignedToast_one: "{{count}} item assigned to {{name}}",
    assignedToast_other: "{{count}} items assigned to {{name}}",
  },
  detail: {
    empty: "Pick a conversation",
    label: "Selected item",
    emptyHint: "Select a comment or message to read the thread, see AI suggestions and reply.",
    back: "Back to list",
    onPost: "Comment on a post",
    viewOnInstagram: "View on Instagram",
    postUnavailable: "Post preview unavailable",
    thread: "Thread",
    noReplies: "No replies yet",
    byAccount: "Your reply",
    hiddenNotice: "This comment is hidden. Only the author and their followers can see it.",
    deletedNotice: "This comment was deleted.",
    labels: "AI categories",
    confidence: "Confidence: {{level}}",
    toxicity: "Toxicity: {{level}}",
    language: "Language",
    receivedAt: "Received",
    assignedTo: "Assigned to {{name}}",
    unassigned: "Unassigned",
    assignTo: "Assign to…",
    assignMe: "Assign to me",
    unassign: "Unassign",
    deleteConfirm: "Delete this comment on Instagram?",
    deleteHint: "It will be removed from the post for everyone. Hiding is reversible; deleting is not.",
    more: "More actions",
    openPost: "Open post",
    why: {
      link: "Why this label?",
      title: "Why Malfak labelled it this way",
      intent: "Topic",
      sentiment: "Sentiment",
      sure: "How sure",
      toxicity: "Rudeness",
      language: "Language",
      low: "Low",
      medium: "Medium",
      high: "High",
      veryLikely: "Very likely",
      likely: "Likely",
      possibly: "Possibly",
      sentence: "{{likelihood}} a {{intent}}",
    },
  },
  actions: {
    hide: "Hide",
    unhide: "Unhide",
    done: "Done",
    reopen: "Reopen",
    assign: "Assign",
    delete: "Delete",
    hidden: "Comment hidden",
    unhidden: "Comment visible again",
    markedDone: "Marked done",
    reopened: "Reopened",
    assigned: "Assigned to {{name}}",
    unassigned: "Unassigned",
    deleted: "Comment deleted",
    failed: "That didn't work. Your change was undone.",
  },
  ai: {
    title: "AI suggestions",
    subtitle: "Drafted in your brand voice",
    insert: "Use",
    regenerate: "Regenerate",
    loading: "Drafting replies…",
    error: "Couldn't draft suggestions",
    shorter: "Shorter",
    formal: "More formal",
    english: "In English",
    arabic: "In Arabic",
    custom: "Tell the AI what to change…",
    suggested: "Suggested reply",
    others: "Other suggestions",
    fewer: "Show less",
    rewrite: "Rewrite it",
  },
  reply: {
    placeholder: "Write a reply…",
    placeholderPrivate: "Write a private message to @{{user}}…",
    public: "Public reply",
    private: "Private reply (DM)",
    privateHint: "Sends a DM to the commenter. Instagram allows one private reply per comment, within {{days}} days.",
    privateUsed: "A private reply was already sent for this comment. Instagram allows only one.",
    privateExpired: "Private replies are only possible within {{days}} days of the comment.",
    privateUnsupported: "This channel doesn't support private replies.",
    send: "Reply",
    sending: "Sending…",
    sent: "Reply sent",
    sentPrivate: "Private reply sent",
    queued: "Sending reply…",
    counter_one: "{{count}} / {{max}}",
    counter_other: "{{count}} / {{max}}",
    shortcut: "Enter to send · Shift+Enter for a new line",
    tooLong: "Too long for Instagram",
    sendingUndo: "Sending in a moment…",
    notSent: "Not sent. Your text is back in the box.",
  },
  dm: {
    windowOpen: "Reply window open",
    windowLeft: "{{time}} left to reply",
    windowClosingSoon: "Closing soon: {{time}} left",
    windowClosed: "Reply window closed",
    windowClosedHint: "Instagram only lets businesses message someone within 24 hours of their last message. You can reply again as soon as {{name}} writes back.",
    windowExplain: "Instagram's 24-hour rule: businesses can reply freely for 24 hours after the customer's last message.",
    aiAgent: "AI agent",
    aiAgentOn: "The AI agent is answering this conversation",
    aiAgentOff: "AI agent off: you're replying manually",
    aiAgentEnabled: "AI agent turned on",
    aiAgentDisabled: "AI agent turned off",
    placeholder: "Message {{name}}…",
    send: "Send",
    sent: "Message sent",
    failed: "Message not sent",
    sentByAi: "AI",
    sentByAutomation: "Automation",
    sentByUser: "Team",
    transcript: "Transcript",
    noTranscript: "No transcript yet",
    play: "Play voice note",
    pause: "Pause",
    quickReplies: "Quick replies",
    quickRepliesHint: "Tappable chips under your message (up to {{max}}, {{len}} characters each).",
    buttons: "Buttons",
    buttonsHint: "Up to {{max}} buttons with a link or a payload.",
    addQuickReply: "Add quick reply",
    addButton: "Add button",
    buttonTitle: "Button title",
    buttonUrl: "https://…",
    quickReplyTitle: "Reply title",
    status: {
      received: "Received",
      sending: "Sending",
      sent: "Sent",
      delivered: "Delivered",
      read: "Seen",
      failed: "Failed",
    },
    today: "Today",
    yesterday: "Yesterday",
    loadOlder: "Load older messages",
  },
  contact: {
    title: "Person",
    identities: "Channels",
    tags: "Tags",
    noTags: "No tags",
    firstSeen: "First seen",
    lastSeen: "Last seen",
    viewProfile: "Open profile",
    show: "Show person details",
    hide: "Hide person details",
    personDetails: "Person details",
    allComments: "All their comments",
  },
  shortcuts: {
    hint: "Shortcuts",
  },
};
